// A time as the server reads it (Archives.Content.EventTime, Archives.Content.Calendar): "1912", "1912-03",
// "1912-03-03", "1912-03-03 21:00"; negative years for BCE. In a calendar of the page's own, the same numbers are read
// with its months and day, and shown with its names ("4 Alturiak 1012 DR, 21:00"). Used by the panels to say how a date
// reads before the page is saved; the server checks again on save.

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** A calendar of the page's own, as the server sends it (null: ordinary dates). */
export type CalendarShape = {
  title: string;
  era: string | null;
  down: boolean;
  hours: number;
  minutes: number;
  months: Array<{ name: string | null; days: number }>;
};

let current: CalendarShape | null = null;

/** The calendar dates on this page are written in (from the editor's state); null for ordinary dates. */
export function useCalendar(calendar: CalendarShape | null | undefined): void {
  current = calendar ?? null;
}

export function currentCalendar(): CalendarShape | null {
  return current;
}

const daysIn = (year: number, month: number): number =>
  month === 2 ? (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28) : [4, 6, 9, 11].includes(month) ? 30 : 31;

const yearText = (year: number): string => (year > 0 ? String(year) : `${1 - year} BCE`);

const PATTERN = /^(-?\d{1,5})(?:-(\d{1,3})(?:-(\d{1,5})(?:[ T](\d{1,3}):(\d{2,3}))?)?)?$/;

/** How a written time reads ("3 March 1912, 21:00"), or null when the server wouldn't read it. */
export function readWhen(text: string, calendar: CalendarShape | null = current): string | null {
  const span = readSpan(text, calendar);
  if (span !== undefined) return span;
  const m = PATTERN.exec(text.trim());
  if (!m) return null;
  return calendar ? readIn(calendar, m) : readPlain(m);
}

const ordinal = (n: number): string => `${n}${[11, 12, 13].includes(n % 100) ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th"}`;

// Decades and centuries: "1910s", "490s BCE", "19th century", "5th century BCE" (BCE only in ordinary dates).
// Undefined when the text isn't one at all.
function readSpan(text: string, calendar: CalendarShape | null): string | null | undefined {
  const era = calendar?.era ? ` ${calendar.era}` : "";
  const decade = /^(\d{1,4})0s(\s*BCE)?$/i.exec(text.trim());
  if (decade) return decade[2] && calendar ? null : `the ${Number(decade[1]) * 10}s${decade[2] ? " BCE" : era}`;
  const century = /^(\d{1,3})(st|nd|rd|th)\s+century(\s*BCE)?$/i.exec(text.trim());
  if (!century) return undefined;
  const n = Number(century[1]);
  if (n < 1 || ordinal(n) !== `${n}${century[2]!.toLowerCase()}` || (century[3] && calendar)) return null;
  return `the ${ordinal(n)} century${century[3] ? " BCE" : era}`;
}

function readPlain(m: RegExpExecArray): string | null {
  if ((m[2]?.length ?? 0) > 2 || (m[3]?.length ?? 0) > 2 || (m[4]?.length ?? 0) > 2 || (m[5]?.length ?? 0) > 2) return null;
  const year = Number(m[1]);
  if (m[2] === undefined) return yearText(year);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  if (m[3] === undefined) return `${MONTHS[month - 1]} ${yearText(year)}`;
  const day = Number(m[3]);
  if (day < 1 || day > daysIn(year, month)) return null;
  const date = `${day} ${MONTHS[month - 1]} ${yearText(year)}`;
  if (m[4] === undefined) return date;
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  if (hour > 23 || minute > 59) return null;
  return `${date}, ${String(hour).padStart(2, "0")}:${m[5]}`;
}

function readIn(calendar: CalendarShape, m: RegExpExecArray): string | null {
  const year = Number(m[1]);
  const y = calendar.era ? `${year} ${calendar.era}` : String(year);
  if (m[2] === undefined) return y;
  const month = Number(m[2]);
  const part = calendar.months[month - 1];
  if (!part) return null;
  if (m[3] === undefined) return part.name ? `${part.name} ${y}` : `month ${month}, ${y}`;
  const day = Number(m[3]);
  if (day < 1 || day > part.days) return null;
  const date = part.name ? (part.days === 1 ? `${part.name} ${y}` : `${day} ${part.name} ${y}`) : `day ${day} of month ${month}, ${y}`;
  if (m[4] === undefined) return date;
  const hour = Number(m[4]);
  const minute = Number(m[5]);
  if (hour >= calendar.hours || minute >= calendar.minutes) return null;
  const hourWidth = Math.max(2, String(calendar.hours - 1).length);
  const minuteWidth = Math.max(2, String(calendar.minutes - 1).length);
  return `${date}, ${String(hour).padStart(hourWidth, "0")}:${String(minute).padStart(minuteWidth, "0")}`;
}

/** A sortable number for comparing two written times (only to warn that an end comes before its start). */
export function whenOrder(text: string, calendar: CalendarShape | null = current): number | null {
  const span = /^(\d{1,4})0s/i.exec(text.trim()) ?? null;
  const century = /^(\d{1,3})(?:st|nd|rd|th)\s+century/i.exec(text.trim());
  if (readWhen(text, calendar) === null) return null;
  // A decade or century sorts from its first year (BCE ones before year 1).
  if (span || century) {
    const bce = /BCE$/i.test(text.trim());
    const first = span ? Number(span[1]) * 10 + (bce ? 9 : 0) : (Number(century![1]) - 1) * 100 + (bce ? 100 : 1);
    return whenOrder(String(bce ? -first : first), calendar);
  }
  const m = PATTERN.exec(text.trim());
  if (!m) return null;
  const [months, days, hours, minutes] = calendar
    ? [calendar.months.length, Math.max(...calendar.months.map((x) => x.days)), calendar.hours, calendar.minutes]
    : [12, 31, 24, 60];
  const year = calendar?.down ? -Number(m[1]) : Number(m[1]);
  return (((year * (months + 1) + Number(m[2] ?? 1)) * (days + 1) + Number(m[3] ?? 1)) * hours + Number(m[4] ?? 0)) * minutes + Number(m[5] ?? 0);
}

/** "Write it like 1912, 1912-03, 1912-03-03 or 1912-03-03 21:00", in the page's calendar. */
export function timeHint(calendar: CalendarShape | null = current): string {
  if (!calendar) return "1912, 1912-03, 1912-03-03 or 1912-03-03 21:00";
  return `numbers in ${calendar.title}: 1012, 1012-03, 1012-03-04 or 1012-03-04 21:00 (month 1–${calendar.months.length})`;
}
